import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Image, Trash2, GripVertical, Plus, Save, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGallery = () => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({ title: '', media_url: '' });
  const [draggedItem, setDraggedItem] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/gallery`);
      const data = await response.json();
      setGallery(data);
    } catch (err) {
      console.error('Error fetching gallery:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;

    const newGallery = [...gallery];
    const draggedItemContent = newGallery[draggedItem];
    newGallery.splice(draggedItem, 1);
    newGallery.splice(index, 0, draggedItemContent);
    
    // Update display_order
    newGallery.forEach((item, i) => {
      item.display_order = i;
    });

    setGallery(newGallery);
    setDraggedItem(index);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      const items = gallery.map((item, index) => ({
        id: item.id,
        display_order: index
      }));

      await fetch(`${API_URL}/api/admin/gallery/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(items)
      });

      setHasChanges(false);
      alert('Gallery order saved!');
    } catch (err) {
      console.error('Error saving order:', err);
      alert('Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  const addItem = async () => {
    if (!newItem.title || !newItem.media_url) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/gallery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: `gallery-${Date.now()}`,
          title: newItem.title,
          media_url: newItem.media_url,
          media_type: 'image',
          gallery_category: 'past_events',
          display_order: gallery.length,
          is_active: true
        })
      });

      if (response.ok) {
        await fetchGallery();
        setNewItem({ title: '', media_url: '' });
        setShowAddForm(false);
      }
    } catch (err) {
      console.error('Error adding item:', err);
      alert('Failed to add item');
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;

    try {
      await fetch(`${API_URL}/api/admin/gallery/${id}`, {
        method: 'DELETE'
      });
      await fetchGallery();
    } catch (err) {
      console.error('Error deleting item:', err);
      alert('Failed to delete item');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Gallery Management</h1>
            <p className="text-gray-400">Drag and drop to reorder photos</p>
          </div>
          <div className="flex gap-2">
            {hasChanges && (
              <Button
                onClick={saveOrder}
                disabled={saving}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Order'}
              </Button>
            )}
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-orange-500 hover:bg-orange-600 text-black"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Photo
            </Button>
          </div>
        </div>

        {/* Add Form Modal */}
        {showAddForm && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Add New Photo</CardTitle>
                <button onClick={() => setShowAddForm(false)} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-white mb-2 text-sm">Title</label>
                <input
                  type="text"
                  value={newItem.title}
                  onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
                  placeholder="Photo title"
                />
              </div>
              <div>
                <label className="block text-white mb-2 text-sm">Image URL</label>
                <input
                  type="text"
                  value={newItem.media_url}
                  onChange={(e) => setNewItem({ ...newItem, media_url: e.target.value })}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-white focus:border-orange-500 focus:outline-none"
                  placeholder="https://..."
                />
              </div>
              {newItem.media_url && (
                <div className="mt-4">
                  <p className="text-gray-400 text-sm mb-2">Preview:</p>
                  <img src={newItem.media_url} alt="Preview" className="max-h-40 rounded" />
                </div>
              )}
              <Button onClick={addItem} className="bg-orange-500 hover:bg-orange-600 text-black w-full">
                Add Photo
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Gallery Grid */}
        {loading ? (
          <div className="text-center text-gray-400 py-12">Loading gallery...</div>
        ) : gallery.length === 0 ? (
          <div className="text-center text-gray-400 py-12">
            <Image className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>No photos in gallery. Add your first photo!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {gallery.map((item, index) => (
              <div
                key={item.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`relative group bg-zinc-900 rounded-lg overflow-hidden border-2 transition-all cursor-move ${
                  draggedItem === index ? 'border-orange-500 opacity-50' : 'border-zinc-800 hover:border-zinc-600'
                }`}
              >
                <div className="aspect-square">
                  <img
                    src={item.media_url}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <div className="text-white text-center p-2">
                    <GripVertical className="w-6 h-6 mx-auto mb-1" />
                    <p className="text-xs">Drag to reorder</p>
                  </div>
                </div>

                {/* Delete Button */}
                <button
                  onClick={() => deleteItem(item.id)}
                  className="absolute top-2 right-2 p-2 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                >
                  <Trash2 className="w-4 h-4 text-white" />
                </button>

                {/* Order Badge */}
                <div className="absolute top-2 left-2 bg-orange-500 text-black text-xs font-bold px-2 py-1 rounded">
                  #{index + 1}
                </div>

                {/* Title */}
                <div className="p-2 bg-zinc-900">
                  <p className="text-white text-sm truncate">{item.title}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminGallery;
